import { ENGLISH_UI_LABELS, type AppLocale } from './locale';

export type BillingErrorsCopy = {
  purchaseUnavailable: string;
  manageUnavailable: string;
  storePurchaseNoInventory: string;
  storePurchaseNotCompleted: string;
  storePurchaseFailed: string;
  storeLicenseNotReflected: string;
  billingApiError: (status: number) => string;
  checkoutUrlMissing: string;
  statusCheckFailed: (status: number) => string;
  redeemInvalidCode: string;
  redeemIssueUnavailable: string;
  redeemIssueFailed: string;
  billingNetworkError: string;
};

export type AppCopy = {
  menu: {
    new: string;
    import: string;
  };
  placeholder: {
    noTitle: string;
  };
  empty: {
    selectOrCreate: string;
  };
  toolbar: {
    grid: string;
    guide: string;
  };
  help: {
    quickHelp: string;
    instruction: string;
  };
  cloneSuffix: string;
  default: {
    sheetTitle: (n: number) => string;
    importedSheet: string;
    unknownSheet: string;
  };
  stats: {
    total: string;
    count: string;
    average: string;
    max: string;
    min: string;
  };
  group: {
    subtotal: string;
    total: string;
  };
  importConfirm: {
    title: string;
    message: (title: string) => string;
  };
  export: {
    title: string;
    message: (title: string) => string;
    privateShare: string;
    publicShare: string;
    download: string;
  };
  importPrompt: {
    title: string;
    message: string;
    placeholder: string;
    selectFile: string;
    fromUrl: string;
  };
  common: {
    cancel: string;
    import: string;
  };
  meta: {
    importSheet: (name: string) => string;
  };
  error: {
    importFailed: string;
    saveFailed: string;
    invalidFileFormat: string;
    invalidUrl: string;
    readFileFailed: string;
    openFailed: string;
  };
  table: {
    clear: string;
    copy: string;
    copied: string;
    paste: string;
    pasted: string;
    xlsx: string;
    header: {
      title: string;
      qty: string;
      amountSuffix: string;
      groupTitle: string;
      pouchTitle: string;
      value: string;
      opSign: string;
      multiplier: string;
      cell: string;
    };
    ungrouped: string;
    misc: string;
    groupN: (n: number) => string;
    groupTotalSuffix: (name: string) => string;
    excel: {
      filename: string;
      columns: string;
      totalRow: string;
    };
  };
  layer: {
    calcAria: string;
    removeAria: (name: string) => string;
    addAria: string;
    defaultName: string;
  };
  keyboard: {
    backspace: string;
    confirm: string;
    cancel: string;
    editTitle: string;
    moveKeyboard: string;
  };
  welcome: {
    sheetTitle: string;
    pouch1: string;
    pouch2: string;
    pouch3: string;
    pouch4: string;
    group1Title: string;
    group1Placeholder: string;
    group2Title: string;
    group2Placeholder: string;
    defaultLayerName: string;
    historyInitial: string;
    historyContent: string;
  };
  billingErrors: BillingErrorsCopy;
};

const makeBillingErrors = (
  purchaseUnavailable: string,
  manageUnavailable: string,
  storePurchaseNoInventory: string,
  storePurchaseNotCompleted: string,
  storePurchaseFailed: string,
  storeLicenseNotReflected: string,
  billingApiError: (status: number) => string,
  checkoutUrlMissing: string,
  statusCheckFailed: (status: number) => string,
  redeemInvalidCode: string,
  redeemIssueUnavailable: string,
  redeemIssueFailed: string,
  billingNetworkError: string,
): BillingErrorsCopy => ({
  purchaseUnavailable,
  manageUnavailable,
  storePurchaseNoInventory,
  storePurchaseNotCompleted,
  storePurchaseFailed,
  storeLicenseNotReflected,
  billingApiError,
  checkoutUrlMissing,
  statusCheckFailed,
  redeemInvalidCode,
  redeemIssueUnavailable,
  redeemIssueFailed,
  billingNetworkError,
});

export const APP_COPY: Record<AppLocale, AppCopy> = {
  en: {
    menu: { new: 'New', import: 'Import' },
    placeholder: { noTitle: 'No Title' },
    empty: { selectOrCreate: 'Select a sheet or create a new sheet' },
    toolbar: { grid: 'Grid', guide: 'Guide' },
    help: { quickHelp: 'Quick Help', instruction: 'Instruction' },
    cloneSuffix: '- copy',
    default: {
      sheetTitle: (n) => `Sheet ${n}`,
      importedSheet: 'Imported Sheet',
      unknownSheet: 'Unknown Sheet',
    },
    stats: {
      total: ENGLISH_UI_LABELS.TOTAL,
      count: ENGLISH_UI_LABELS.COUNT,
      average: ENGLISH_UI_LABELS.AVERAGE,
      max: ENGLISH_UI_LABELS.MAX,
      min: ENGLISH_UI_LABELS.MIN,
    },
    group: {
      subtotal: ENGLISH_UI_LABELS.SUBTOTAL,
      total: ENGLISH_UI_LABELS.TOTAL,
    },
    importConfirm: {
      title: 'Import Confirmation',
      message: (title) => `Import POUCHES Sheet: ${title}?`,
    },
    export: {
      title: 'Export POUCHES Sheet',
      message: (title) => `How would you like to export ${title}?`,
      privateShare: 'Private Share',
      publicShare: 'Public Share',
      download: 'Download',
    },
    importPrompt: {
      title: 'Import POUCHES Sheet',
      message:
        'Enter import code or full URL, or leave empty to select .pouches file:',
      placeholder: 'e.g., 1756489555964_sah6mrm5bfe',
      selectFile: 'Select File...',
      fromUrl: 'Import from URL',
    },
    common: { cancel: 'Cancel', import: 'Import' },
    meta: {
      importSheet: (name) => `Import POUCHES sheet: "${name}"`,
    },
    error: {
      importFailed: 'Import Failed',
      saveFailed: 'Save failed.',
      invalidFileFormat:
        'Invalid file format. Please select a valid .pouches file.',
      invalidUrl: 'Invalid URL format. Please use the correct import URL.',
      readFileFailed: 'Failed to read file.',
      openFailed: 'Open failed.',
    },
    table: {
      clear: 'Clear',
      copy: 'Copy',
      copied: 'Copied!',
      paste: 'Paste',
      pasted: 'Pasted!',
      xlsx: 'XLSX',
      header: {
        title: 'Title',
        qty: 'Qty',
        amountSuffix: ENGLISH_UI_LABELS.SUBTOTAL,
        groupTitle: 'Group Title',
        pouchTitle: 'Pouch Title',
        value: 'Value',
        opSign: '×/÷ Sign',
        multiplier: 'Qty',
        cell: 'Cell',
      },
      ungrouped: 'Ungrouped',
      misc: 'misc.',
      groupN: (n) => `Group ${n}`,
      groupTotalSuffix: (name) => `(${name} Total)`,
      excel: {
        filename: 'TableData.xlsx',
        columns: 'Group, Title, Value, Op, Mul/Div, Subtotal',
        totalRow: 'Total',
      },
    },
    layer: {
      calcAria: 'Calculation Layers',
      removeAria: (name) => `Remove layer "${name}"`,
      addAria: 'Add layer',
      defaultName: 'Value',
    },
    keyboard: {
      backspace: 'Backspace',
      confirm: 'Confirm',
      cancel: 'Cancel',
      editTitle: 'Edit title',
      moveKeyboard: 'Move keyboard',
    },
    welcome: {
      sheetTitle: 'Welcome to POUCHES! - This is the sheet',
      pouch1: "I'm a pouch",
      pouch2: 'Double-tap sheet to add',
      pouch3: 'Drag to move',
      pouch4: 'Long press to clone',
      group1Title: 'This is a group - Drag sheet to add',
      group1Placeholder: 'Group 1',
      group2Title: 'Can overlap',
      group2Placeholder: 'Group 2',
      defaultLayerName: 'This is the layer',
      historyInitial: 'Initial state',
      historyContent: 'Welcome content',
    },
    billingErrors: makeBillingErrors(
      'Purchases are not available on this platform yet.',
      'Subscription management is not available on this platform yet.',
      'This subscription is not available from Microsoft Store yet. Please try again later.',
      'Purchase was cancelled.',
      'Purchase failed. Please check the Microsoft Store and try again.',
      'Purchase was completed, but the license is taking time to reflect. Please try "Restore" after a moment.',
      (status) => `Billing API error (${status})`,
      'Checkout URL was not returned.',
      (status) => `Status check failed (${status})`,
      'Enter a valid 12-digit redeem code.',
      'An active App Store subscription is required to issue a code.',
      'Could not issue a Web redeem code.',
      'Could not reach the billing server. Check your network connection.',
    ),
  },
  ja: {
    menu: { new: '新規', import: 'インポート' },
    placeholder: { noTitle: 'タイトルなし' },
    empty: { selectOrCreate: 'シートを選択するか、新しいシートを作成してください' },
    toolbar: { grid: 'グリッド', guide: 'ガイド' },
    help: { quickHelp: 'クイックヘルプ', instruction: '説明書' },
    cloneSuffix: ' - コピー',
    default: {
      sheetTitle: (n) => `シート ${n}`,
      importedSheet: 'インポートしたシート',
      unknownSheet: '不明なシート',
    },
    stats: {
      total: ENGLISH_UI_LABELS.TOTAL,
      count: ENGLISH_UI_LABELS.COUNT,
      average: ENGLISH_UI_LABELS.AVERAGE,
      max: ENGLISH_UI_LABELS.MAX,
      min: ENGLISH_UI_LABELS.MIN,
    },
    group: {
      subtotal: ENGLISH_UI_LABELS.SUBTOTAL,
      total: ENGLISH_UI_LABELS.TOTAL,
    },
    importConfirm: {
      title: 'インポートの確認',
      message: (title) => `POUCHES シートをインポートしますか: ${title}？`,
    },
    export: {
      title: 'POUCHES シートのエクスポート',
      message: (title) => `${title} をどのようにエクスポートしますか？`,
      privateShare: 'プライベート共有',
      publicShare: 'パブリック共有',
      download: 'ダウンロード',
    },
    importPrompt: {
      title: 'POUCHES シートのインポート',
      message:
        'インポートコードまたは URL を入力するか、空欄のまま .pouches ファイルを選択してください:',
      placeholder: '例: 1756489555964_sah6mrm5bfe',
      selectFile: 'ファイルを選択...',
      fromUrl: 'URL からインポート',
    },
    common: { cancel: 'キャンセル', import: 'インポート' },
    meta: {
      importSheet: (name) => `POUCHES シートをインポート: 「${name}」`,
    },
    error: {
      importFailed: 'インポートに失敗しました',
      saveFailed: '保存に失敗しました。',
      invalidFileFormat:
        '無効なファイル形式です。有効な .pouches ファイルを選択してください。',
      invalidUrl: '無効な URL 形式です。正しいインポート URL を使用してください。',
      readFileFailed: 'ファイルの読み込みに失敗しました。',
      openFailed: '開けませんでした。',
    },
    table: {
      clear: 'クリア',
      copy: 'コピー',
      copied: 'コピーしました！',
      paste: 'ペースト',
      pasted: 'ペーストしました！',
      xlsx: 'XLSX',
      header: {
        title: 'タイトル',
        qty: '数量',
        amountSuffix: '小計',
        groupTitle: 'グループ名',
        pouchTitle: 'パウチ名',
        value: '数値',
        opSign: '×/÷ 符号',
        multiplier: '数量',
        cell: 'セル',
      },
      ungrouped: 'グループなし',
      misc: 'その他',
      groupN: (n) => `グループ ${n}`,
      groupTotalSuffix: (name) => `（${name} 合計）`,
      excel: {
        filename: 'TableData.xlsx',
        columns: 'グループ, 名前, 数値, 演算, 乗/除, 小計',
        totalRow: '合計',
      },
    },
    layer: {
      calcAria: '計算レイヤー',
      removeAria: (name) => `レイヤー「${name}」を削除`,
      addAria: 'レイヤーを追加',
      defaultName: '値',
    },
    keyboard: {
      backspace: 'Backspace',
      confirm: '確定',
      cancel: 'キャンセル',
      editTitle: 'パウチ名を編集',
      moveKeyboard: 'キーボードを移動',
    },
    welcome: {
      sheetTitle: 'ようこそPOUCHESへ！ - ここがシートです',
      pouch1: '私がパウチです',
      pouch2: 'シートをダブルで押して追加',
      pouch3: 'ドラッグして移動',
      pouch4: '長押しで複製',
      group1Title: 'ここがグループです - シートをドラッグして追加',
      group1Placeholder: 'グループ 1',
      group2Title: '重なれます',
      group2Placeholder: 'グループ 2',
      defaultLayerName: 'ここはレイヤーです',
      historyInitial: '初期状態',
      historyContent: 'ウェルカムコンテンツ',
    },
    billingErrors: makeBillingErrors(
      'このプラットフォームではまだ購入できません。',
      'このプラットフォームではまだサブスクリプションを管理できません。',
      'Microsoft Store ではまだこのサブスクリプションを購入できません。しばらく待ってから再度お試しください。',
      '購入をキャンセルしました。',
      '購入に失敗しました。Microsoft Store の状態を確認してから再度お試しください。',
      '購入は完了しましたが、ライセンスの反映に時間がかかっています。しばらくしてから「復元」をお試しください。',
      (status) => `課金 API エラー (${status})`,
      'チェックアウト URL が返されませんでした。',
      (status) => `ステータス確認に失敗しました (${status})`,
      '有効な12桁の引き換えコードを入力してください。',
      'コードを発行するには App Store の有効なサブスクが必要です。',
      'Web版用コードを発行できませんでした。',
      '課金サーバーに接続できませんでした。ネットワーク接続を確認してください。',
    ),
  },
  zh: {
    menu: { new: '新建', import: '导入' },
    placeholder: { noTitle: '无标题' },
    empty: { selectOrCreate: '请选择工作表或创建新工作表' },
    toolbar: { grid: '网格', guide: '指南' },
    help: { quickHelp: '快速帮助', instruction: '说明' },
    cloneSuffix: ' - 副本',
    default: {
      sheetTitle: (n) => `工作表 ${n}`,
      importedSheet: '导入的工作表',
      unknownSheet: '未知工作表',
    },
    stats: {
      total: ENGLISH_UI_LABELS.TOTAL,
      count: ENGLISH_UI_LABELS.COUNT,
      average: ENGLISH_UI_LABELS.AVERAGE,
      max: ENGLISH_UI_LABELS.MAX,
      min: ENGLISH_UI_LABELS.MIN,
    },
    group: {
      subtotal: ENGLISH_UI_LABELS.SUBTOTAL,
      total: ENGLISH_UI_LABELS.TOTAL,
    },
    importConfirm: {
      title: '导入确认',
      message: (title) => `导入 POUCHES 工作表：${title}？`,
    },
    export: {
      title: '导出 POUCHES 工作表',
      message: (title) => `如何导出 ${title}？`,
      privateShare: '私密分享',
      publicShare: '公开分享',
      download: '下载',
    },
    importPrompt: {
      title: '导入 POUCHES 工作表',
      message: '输入导入代码或完整 URL，或留空以选择 .pouches 文件：',
      placeholder: '例如：1756489555964_sah6mrm5bfe',
      selectFile: '选择文件...',
      fromUrl: '从 URL 导入',
    },
    common: { cancel: '取消', import: '导入' },
    meta: {
      importSheet: (name) => `导入 POUCHES 工作表："${name}"`,
    },
    error: {
      importFailed: '导入失败',
      saveFailed: '保存失败。',
      invalidFileFormat: '无效的文件格式。请选择有效的 .pouches 文件。',
      invalidUrl: '无效的 URL 格式。请使用正确的导入 URL。',
      readFileFailed: '读取文件失败。',
      openFailed: '打开失败。',
    },
    table: {
      clear: '清除',
      copy: '复制',
      copied: '已复制！',
      paste: '粘贴',
      pasted: '已粘贴！',
      xlsx: 'XLSX',
      header: {
        title: '标题',
        qty: '数量',
        amountSuffix: '小计',
        groupTitle: '组名',
        pouchTitle: '袋名',
        value: '数值',
        opSign: '×/÷ 符号',
        multiplier: '数量',
        cell: '单元格',
      },
      ungrouped: '未分组',
      misc: '其他',
      groupN: (n) => `分组 ${n}`,
      groupTotalSuffix: (name) => `（${name} 合计）`,
      excel: {
        filename: 'TableData.xlsx',
        columns: '组, 名称, 数值, 运算, 乘/除, 小计',
        totalRow: '合计',
      },
    },
    layer: {
      calcAria: '计算图层',
      removeAria: (name) => `删除图层「${name}」`,
      addAria: '添加图层',
      defaultName: '值',
    },
    keyboard: {
      backspace: 'Backspace',
      confirm: '确认',
      cancel: '取消',
      editTitle: '编辑袋名',
      moveKeyboard: '移动键盘',
    },
    welcome: {
      sheetTitle: '欢迎使用 POUCHES！ - 这里是工作表',
      pouch1: '我是小袋',
      pouch2: '双击工作表以添加',
      pouch3: '拖动以移动',
      pouch4: '长按以复制',
      group1Title: '这里是分组 - 拖动工作表以添加',
      group1Placeholder: '分组 1',
      group2Title: '可以重叠',
      group2Placeholder: '分组 2',
      defaultLayerName: '这里是图层',
      historyInitial: '初始状态',
      historyContent: '欢迎内容',
    },
    billingErrors: makeBillingErrors(
      '此平台尚不支持购买。',
      '此平台尚不支持管理订阅。',
      'Microsoft Store 尚无法购买此订阅。请稍后再试。',
      '已取消购买。',
      '购买失败。请检查 Microsoft Store 状态后重试。',
      '购买已完成，但许可证反映需要一些时间。请稍后点击「恢复」。',
      (status: number) => `计费 API 错误 (${status})`,
      '未返回结账 URL。',
      (status: number) => `状态检查失败 (${status})`,
      '请输入有效的12位兑换码。',
      '需要有效的 App Store 订阅才能发放兑换码。',
      '无法发放 Web 版兑换码。',
      '无法连接计费服务器。请检查网络连接。',
    ),
  },
};

export function getAppCopyForLocale(locale: AppLocale): AppCopy {
  return APP_COPY[locale];
}

/** @deprecated Use nested fields on AppCopy, e.g. copy.default.sheetTitle(n) */
export function sheetTitle(locale: AppLocale, n: number): string {
  return APP_COPY[locale].default.sheetTitle(n);
}

export function importConfirmMessage(locale: AppLocale, title: string): string {
  return APP_COPY[locale].importConfirm.message(title);
}

export function exportMessage(locale: AppLocale, title: string): string {
  return APP_COPY[locale].export.message(title);
}

export function metaImportSheet(locale: AppLocale, name: string): string {
  return APP_COPY[locale].meta.importSheet(name);
}

export function tableGroupN(locale: AppLocale, n: number): string {
  return APP_COPY[locale].table.groupN(n);
}

export function tableGroupTotalSuffix(locale: AppLocale, name: string): string {
  return APP_COPY[locale].table.groupTotalSuffix(name);
}

export function layerRemoveAria(locale: AppLocale, name: string): string {
  return APP_COPY[locale].layer.removeAria(name);
}
